import { inject, injectable } from "@launchtray/tsyringe-async";
import { ModuleFlowExecutor } from "by/gamefactory/cityversetycoon/composition/module/ModuleFlowExecutor";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { SceneManager } from "by/gamefactory/scene/SceneManager";
import { Viewable } from "by/gamefactory/scene/viewable/Viewable";
import { IResourcePackage } from "resources/IResourcePackage";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";
import { IFirstTimeExperienceController } from "../../common/controller/IFirstTimeExperienceController";
import { IFirstTimeExperienceModel } from "../../common/models/IFirstTimeExperienceModel";
import { IPlayerModel } from "../../common/models/IPlayerModel";
import { NoCacheDynamicPackage } from "../../common/resources/NoCacheDynamicPackage";
import { MarketplaceModule } from "../marketplace/MarketplaceModule";
import { NotificationMessageType } from "./NotificationMessageType";
import { NotificationMessagesModule } from "./NotificationMessagesModule";
import { TournamentIntroPopupSceneFactory, UnboardingPopupSceneFactory, YouNoMoreOwnerPopupSceneFactory } from "./NotificationModuleFactories";
import { UnboardingType } from "./components/unboarding/UnboardingType";
import { TournamentIntroResourcePackage } from "./resources/TournamentIntroResourcePackage";
import { UnboardingResourcePackage } from "./resources/UnboardingResourcePackage";
import { YouNoMoreOwnerResourcePackage } from "./resources/YouNoMoreOwnerResourcePackage";
import { UnboardingSceneOptions } from "./scenes/UnboardingPopupScene";
import { YouNoMoreOwnerSceneResult } from "./scenes/YouNoMoreOwnerSceneResult";
import { TopPanel } from "../../game/components/toppanel/TopPanel";

@injectable()
export class NotificationMessagesModuleExecutor extends ModuleFlowExecutor<NotificationMessagesModule> {
    private static readonly NO_CACHE_TAG = "unboarding_buildings";

    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly sceneManager: SceneManager;
    private readonly firstTimeExperienceController: IFirstTimeExperienceController;
    private readonly playerModel: IPlayerModel;
    private readonly firstTimeExperienceModel: IFirstTimeExperienceModel;
    private readonly topPanel: TopPanel;
    private readonly logger: ILogger;
    private resourcePackage: IResourcePackage;
    private buildingsDynamicResources: NoCacheDynamicPackage;

    public constructor(
        @inject("IFirstTimeExperienceController") firstTimeExperienceController: IFirstTimeExperienceController,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        @inject("IFirstTimeExperienceModel") firstTimeExperienceModel: IFirstTimeExperienceModel,
        asyncOperationNotification: AsyncOperationNotification,
        sceneManager: SceneManager,
        topPanel: TopPanel,
        @inject("ILogger") logger: ILogger
    ) {
        super();
        this.asyncOperationNotification = asyncOperationNotification;
        this.firstTimeExperienceController = firstTimeExperienceController;
        this.playerModel = playerModel;
        this.firstTimeExperienceModel = firstTimeExperienceModel;
        this.sceneManager = sceneManager;
        this.topPanel = topPanel;
        this.logger = logger;
    }

    public override async execute(module: NotificationMessagesModule, type?: NotificationMessageType): Promise<void> {
        try {
            this.asyncOperationNotification.show();
            const scene = await this.resolveSceneByMessageType(module, type);
            this.asyncOperationNotification.hide();
            this.topPanel.bringToOriginParent();
            const result = await this.sceneManager.showViewableAsync(scene);
            if (result === YouNoMoreOwnerSceneResult.SHOW_MARKETPLACE) {
                await this.showMarketplace(module);
            }
            if (this.firstTimeExperienceModel.tooltipsInfo.value.ownerWithoutBuildings && type === NotificationMessageType.YOU_NO_MORE_OWNER) {
                await this.firstTimeExperienceController.sendShownTooltip({ tooltips: { ownerWithoutBuildings: false } });
                this.firstTimeExperienceController.getFirstTimeExperience();
            }
            if (this.firstTimeExperienceModel.becameOwner.value && type === NotificationMessageType.UNBOARDING_OWNER) {
                await this.firstTimeExperienceController.sendShownTooltip({ becameOwner: false });
                this.firstTimeExperienceController.getFirstTimeExperience();
            }
            if (this.firstTimeExperienceModel.becameRenter.value && type === NotificationMessageType.UNBOARDING_RENTER) {
                await this.firstTimeExperienceController.sendShownTooltip({ becameRenter: false });
                this.firstTimeExperienceController.getFirstTimeExperience();
            }
        } catch (e: unknown) {
            this.asyncOperationNotification.hide();
            this.logger.error(`Exception on executing NotificationMessagesModule flow: `, e);
        }
        finally {
            this.resourcePackage.destroy();
            if (this.buildingsDynamicResources?.loaded) {
                this.buildingsDynamicResources.destroy();
            }
        }
    }

    private async resolveSceneByMessageType(module: NotificationMessagesModule, type?: NotificationMessageType): Promise<Viewable<void | YouNoMoreOwnerSceneResult>> {
        switch (type) {
            case NotificationMessageType.UNBOARDING_OWNER:
            case NotificationMessageType.UNBOARDING_RENTER: {
                this.resourcePackage = new UnboardingResourcePackage();
                const sceneFactory: UnboardingPopupSceneFactory = await module.resolve("UnboardingPopupSceneFactory");
                const sceneOptions: UnboardingSceneOptions = {
                    type: type === NotificationMessageType.UNBOARDING_OWNER ? UnboardingType.OWNER : UnboardingType.RENTER,
                    buildingsResources: null
                };
                const resourcesToLoad = this.playerModel.buildings.value.flatMap((building) => {
                    return [building?.imageUrl.value, ...building.upgrades.map(upgradeItem => upgradeItem?.imageUrl.value)];
                });
                this.buildingsDynamicResources = new NoCacheDynamicPackage(NotificationMessagesModuleExecutor.NO_CACHE_TAG);
                for (const imageUrl of resourcesToLoad) {
                    this.buildingsDynamicResources.add(imageUrl);
                }
                await this.resourcePackage.load();
                if (resourcesToLoad.length > 0) {
                    await this.buildingsDynamicResources.load();
                }
                sceneOptions.buildingsResources = this.buildingsDynamicResources;
                return await sceneFactory(sceneOptions, this.resourcePackage as UnboardingResourcePackage);
            }
            case NotificationMessageType.YOU_NO_MORE_OWNER: {
                this.resourcePackage = new YouNoMoreOwnerResourcePackage();
                const factory: YouNoMoreOwnerPopupSceneFactory = await module.resolve("YouNoMoreOwnePopupSceneFactory");
                await this.resourcePackage.load();
                return await factory(this.resourcePackage as YouNoMoreOwnerResourcePackage);
            }
            case NotificationMessageType.TOURNAMENT_INTRO: {
                this.resourcePackage = new TournamentIntroResourcePackage();
                const factory: TournamentIntroPopupSceneFactory = await module.resolve("TournamentIntroPopupSceneFactory");
                await this.resourcePackage.load();
                return await factory(this.resourcePackage as TournamentIntroResourcePackage);
            }
            default:
                throw new Error(`Attempt to resolve not implemented notification scene by type: ${type}.`);
        }
    }

    private async showMarketplace(module: NotificationMessagesModule): Promise<void> {
        const marketplaceModule =  await this.asyncOperationNotification.autoShowHide(module.resolve(MarketplaceModule));
        await marketplaceModule.start(-1);
    }
}
